import Form from "@/components/Form";
import useTrack from "@/hooks/useTrack";
import fetchApi from "@/lib/fetchApi";
import { signIn } from "next-auth/react";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

const SignUpForm = ({ organisation }) => {
  const track = useTrack();

  const router = useRouter();

  const [flashMessage, setFlashMessage] = useState({
    message: null,
    type: null,
  });
  const [isSigningUp, setIsSigningUp] = useState(false);

  const {
    register,
    handleSubmit,
    clearErrors,
    setError,
    formState: { isSubmitting, isValidating, errors },
  } = useForm();

  const handleSignIn = async (username, password) => {
    try {
      // If successful, we sign in
      const { ok, error } = await signIn("credentials", {
        username: username,
        password: password,
        redirect: false,
      });

      if (ok && !error) router.push("/welcome");

      if (ok && !error) {
        track("Signed Up");
      }

      if (error) alert(error);
    } catch (err) {
      console.log(err);
    }
  };

  const handleSignUp = async (data) => {
    setIsSigningUp(true);

    try {
      const result = await fetchApi("/api/auth/user/new", {
        method: "POST",
        body: JSON.stringify({
          email: data.email,
          password: data.password,
          fullName: data.fullName,
          organisation: parseInt(organisation.id, 10),
        }),
      });

      // Make sure we have success = true
      if (result.success && !!result.user) {
        return handleSignIn(data.email, data.password);
      } else {
        // Otherwise, we just have to show a generic error
        setFlashMessage({
          message:
            result.message || "There was a problem updating your details",
          type: "error",
        });
      }
    } catch (error) {
      // If we've been provided with errors, loop through them
      if (error.errors) {
        Object.entries(error.errors).forEach(([key, message]) => {
          setError(key, { type: "manual", message });
        });
      }

      setFlashMessage({
        message:
          error.message ||
          "There was a problem signing up with this email address. It may already be in our system. <a href='mailto:hello@ycn.org'>Please drop us an email</a>, who will swiftly troubleshoot.",
        type: "error",
      });
    } finally {
      setIsSigningUp(false);
    }
  };

  const onSubmit = (data) => {
    clearErrors();
    setFlashMessage({ message: null, type: null });
    handleSignUp(data);
  };

  // If we have an error in the query string, show it
  useEffect(() => {
    if (!router.query?.error) return;

    setError("password", {
      type: "manual",
      message: router.query.error,
    });
  }, [router, setError]);

  return (
    <Form
      onSubmit={handleSubmit(onSubmit)}
      buttonLabel="Sign Up"
      busyButtonLabel="Signing up..."
      showLoaderOnSubmitted
      size={"sm"}
      isBusy={isSubmitting || isValidating || isSigningUp}
    >
      <Form.InputField
        name="fullName"
        label="Full name"
        type="text"
        {...register("fullName", { required: "Please enter your full name" })}
        error={errors?.fullName?.message}
      />
      <Form.InputField
        name="email"
        label="Email address"
        type="email"
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...register("email", {
          required: "Please enter a valid email address",
          pattern: {
            value:
              /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/,
            message: "Please enter a valid email address",
          },
        })}
        error={errors?.email?.message}
      />
      <Form.InputField
        name="password"
        label="Password"
        type="password"
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...register("password", {
          required: "Please enter your password",
          minLength: {
            value: 6,
            message:
              "Please choose a password that is at least 6 characters long",
          },
        })}
        error={errors?.password?.message}
      />
    </Form>
  );
};

export default SignUpForm;
